// Firebase client SDK init (web only — the Electron build never imports
// this). Email/password auth. Config comes from VITE_FIREBASE_* env, baked
// in at build time by Vite. These values are NOT secrets — the Firebase web
// config is public by design; access control lives in the server's token
// verification + the SUPERADMIN_EMAILS allowlist.
//
// When VITE_FIREBASE_API_KEY is absent the app runs in **no-login mode**: we
// skip Firebase init entirely (initializeApp with an empty config throws) and
// export safe stubs. The backend accepts unauthenticated requests as a shared
// workspace user in this mode (AUTH_DISABLED). See apiserver/README.md.

import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// True only when a real Firebase web config is present.
export const AUTH_ENABLED = !!config.apiKey;

let auth = null;

if (AUTH_ENABLED) {
  const app = initializeApp(config);
  auth = getAuth(app);

  // Keep the session across reloads (default already, but explicit so the
  // intent is clear and a future "remember me" toggle has an obvious home).
  setPersistence(auth, browserLocalPersistence).catch(() => {});
}

export function signIn(email, password) {
  if (!auth) return Promise.reject(new Error('Auth is disabled (no Firebase configured).'));
  return signInWithEmailAndPassword(auth, email, password);
}

// Google sign-in. A first-time Google user is created in Firebase without the
// `approved` claim, so the server treats them as a pending account and they
// land on the "waiting for approval" screen — the same as email sign-up.
export function signInWithGoogle() {
  if (!auth) return Promise.reject(new Error('Auth is disabled (no Firebase configured).'));
  const provider = new GoogleAuthProvider();
  // Always offer the account chooser, so a shared machine cannot silently
  // sign in as whoever used it last.
  provider.setCustomParameters({ prompt: 'select_account' });
  return signInWithPopup(auth, provider);
}

// Send the "reset your password" email. Resolves even when the address has no
// account: telling a stranger which emails are registered is a disclosure the
// sign-in screen should not make.
export async function sendPasswordReset(email) {
  if (!auth) throw new Error('Auth is disabled (no Firebase configured).');
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (err) {
    const code = (err && err.code) || '';
    if (code.includes('user-not-found') || code.includes('invalid-email')) return;
    throw err;
  }
}

export function signOutUser() {
  if (!auth) return Promise.resolve();
  return signOut(auth);
}

export function onAuthChanged(cb) {
  if (!auth) {
    // No-login mode: report "no user" once so AuthGate (if ever mounted)
    // resolves instead of hanging on the loading splash.
    Promise.resolve().then(() => cb(null));
    return () => {};
  }
  return onAuthStateChanged(auth, cb);
}

// Current user's ID token, or '' when signed out / auth disabled. The SDK
// returns a cached token and refreshes it near expiry on its own; pass
// forceRefresh when the server has just rejected the cached one.
export async function getIdToken(forceRefresh = false) {
  if (!auth) return '';
  const u = auth.currentUser;
  if (!u) return '';
  try {
    return await u.getIdToken(forceRefresh);
  } catch {
    return '';
  }
}

// Re-authenticate with current password then change to new password.
export async function changePassword(currentPassword, newPassword) {
  if (!auth) throw new Error('Auth is disabled (no Firebase configured).');
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await updatePassword(user, newPassword);
}

export { auth };
